import { useMemo, type ReactNode } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@eis/patterns';
import { manifestBlocks, type ManifestBlock } from '../model/manifest-form';
import { cardinalityOf, manifestRows, partnerRoleRows } from '../model/types';

/**
 * The artifact library: a published version's manifest, grouped by block.
 *
 * Accordions rather than one flat table, because the manifest has four blocks that are read for
 * different reasons — `refs` is what the digest is computed over and what a patch moves,
 * `documents`/`workflows`/`settings` are the promotion work-list. `refs` is therefore open by
 * default and the rest are not: the question "what did this version pin" is asked far more often
 * than "which invoice template does it use".
 *
 * Every ERN is shown service-qualified, never shortened (decision S-5). The prefix is not
 * decoration: the digest is computed over globally unambiguous refs precisely so the same digest
 * cannot mean two things, and a UI that hid the prefix would be undoing that on the one surface
 * where somebody is checking it.
 */
export function ManifestLibrary({
  manifest,
  digest,
  embedded = false,
}: {
  readonly manifest: unknown;
  /** Shown muted: assigned by the service at publish, never something an operator sets. */
  readonly digest?: string;
  /**
   * Inside the selected version's card (design v2's right column, prompt 24): no surface of its
   * own and a smaller heading — the card is the surface.
   */
  readonly embedded?: boolean;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const rows = useMemo(() => manifestRows(manifest), [manifest]);
  const roles = useMemo(() => partnerRoleRows(manifest), [manifest]);

  if (rows.length === 0 && roles.length === 0) {
    return embedded ? (
      <EmptyState compact description={t('tarifs.manifest.empty')} />
    ) : (
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <EmptyState compact description={t('tarifs.manifest.empty')} />
      </Paper>
    );
  }

  // The section is NAMED, which is what makes it a landmark. The same manifest ref legitimately
  // appears elsewhere on a group version's page — a version states its own pins in its summary —
  // so a reader, and a test, needs to be able to say which occurrence they mean.
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby="manifest-library"
      sx={embedded ? { p: 0, border: 'none', bgcolor: 'transparent' } : { p: 2.5 }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}
      >
        <Typography variant={embedded ? 'h4' : 'h3'} component="h2" id="manifest-library">
          {t('tarifs.manifest.title')}
        </Typography>
        {digest !== undefined && digest !== '' && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'monospace' }}
            data-testid="version-digest"
          >
            {t('tarifs.manifest.digest', { digest })}
          </Typography>
        )}
      </Stack>

      {manifestBlocks.map((block) => {
        const entries = rows.filter((row) => row.block === block);
        if (entries.length === 0) return null;
        return (
          <Accordion key={block} defaultExpanded={block === 'refs'} disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} id={`manifest-${block}`}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                {/* `subtitle2` maps onto an <h6>; an accordion label is not a heading. */}
                <Typography variant="subtitle2" component="span">
                  {t(blockLabelKey(block))}
                </Typography>
                <Chip size="small" variant="outlined" label={entries.length} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Table size="small" aria-labelledby={`manifest-${block}`}>
                <TableHead>
                  <TableRow>
                    {/* Asset, the pinned reference — the full, service-qualified ERN (S-5) — and
                        the asset version that reference names. */}
                    <TableCell>{t('tarifs.manifest.asset')}</TableCell>
                    <TableCell>{t('tarifs.manifest.reference')}</TableCell>
                    <TableCell align="right">{t('tarifs.manifest.version')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((row) => (
                    <TableRow key={`${row.block}.${row.key}`}>
                      <TableCell>{row.key}</TableCell>
                      <TableCell>
                        <Box
                          component="code"
                          sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                        >
                          {row.ern}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {assetVersionOf(row.ern)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* The fifth block (SDD-011): the roles a policy on this version may carry, read-only. */}
      {roles.length > 0 && (
        <Accordion disableGutters elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} id="manifest-partnerRoles">
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle2" component="span">
                {t('tarifs.manifest.blocks.partnerRoles')}
              </Typography>
              <Chip size="small" variant="outlined" label={roles.length} />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Table size="small" aria-labelledby="manifest-partnerRoles">
              <TableHead>
                <TableRow>
                  <TableCell>{t('tarifs.manifest.partnerRoles.role')}</TableCell>
                  <TableCell>{t('tarifs.manifest.partnerRoles.ern')}</TableCell>
                  <TableCell>{t('tarifs.manifest.partnerRoles.required')}</TableCell>
                  <TableCell>{t('tarifs.manifest.partnerRoles.cardinality')}</TableCell>
                  <TableCell>{t('tarifs.manifest.partnerRoles.types')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.roleErn}>
                    <TableCell>{role.name}</TableCell>
                    <TableCell>
                      <Box component="code" sx={{ fontFamily: 'monospace' }}>
                        {role.roleErn}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {role.isRequired
                        ? t('tarifs.manifest.partnerRoles.required')
                        : t('tarifs.manifest.partnerRoles.optional')}
                    </TableCell>
                    <TableCell>{cardinalityOf(role)}</TableCell>
                    <TableCell>
                      {role.partnerTypes === undefined
                        ? t('tarifs.manifest.partnerRoles.anyType')
                        : role.partnerTypes.join(', ')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
}

/** `tarifservice:fahrrad/insurance_core.v2` → `v2`; a reference without one reads `—`. */
const assetVersionOf = (ern: string): string => /\.(v[0-9]+)$/.exec(ern)?.[1] ?? '—';

const blockLabelKey = (block: ManifestBlock): `tarifs.manifest.blocks.${ManifestBlock}` =>
  `tarifs.manifest.blocks.${block}`;
